import { useEffect } from "react";
import { useLazyGetSessionIdQuery } from "../../redux/reduxAPI";
import { getPlatformFromStorage } from "../../utils/common";
import { auth } from "../../utils/firebase";
import { useAuthStore } from "stores/auth.store";

const SESSION_ID_KEY = "sessionId";

type Props = {
  creatorId?: number;
};

/**
 * Keeps the creator's analytics session alive. The backend owns the 30-minute
 * sliding window: it reuses the id, or ends the old session and issues a new
 * one. Repeat calls are therefore idempotent.
 *
 * Deliberately gated on a Firebase token rather than `isLoggedIn` — admin
 * sessions must not create creator sessions.
 */
const CreatorSessionTracker = ({ creatorId }: Props) => {
  const { backendLink } = useAuthStore();
  const [triggerGetSessionId, sessionResult] = useLazyGetSessionIdQuery();

  useEffect(() => {
    if (!creatorId) return;

    let cancelled = false;
    (async () => {
      const firebaseToken = await auth?.currentUser?.getIdToken();
      if (cancelled || !firebaseToken) return;

      triggerGetSessionId({
        sessionId: localStorage.getItem(SESSION_ID_KEY) || "",
        platform: getPlatformFromStorage() || "",
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [creatorId, backendLink, triggerGetSessionId]);

  useEffect(() => {
    if (sessionResult.status === "fulfilled" && sessionResult.data?.success) {
      localStorage.setItem(
        SESSION_ID_KEY,
        String(sessionResult.data.sessionId)
      );
    }
  }, [sessionResult]);

  return null;
};

export default CreatorSessionTracker;
